import {
  ActionIcon,
  Avatar,
  Divider,
  Group,
  Menu,
  Stack,
  Text,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconLanguage,
  IconLogout,
  IconMoon,
  IconSettings,
  IconSun,
  IconUser,
} from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { trpc } from '../lib/trpc';

/**
 * Header trailer — color-scheme toggle, language switcher, and a
 * profile menu with avatar / display name / email / mobile.
 *
 * The color-scheme toggle uses Mantine's `useMantineColorScheme` hook
 * which writes to localStorage automatically. Language switching uses
 * react-i18next's `i18n.changeLanguage` and persists via the language
 * detector's localStorage cache.
 */

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'हिन्दी' },
] as const;

export function UserMenu() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const { i18n } = useTranslation();
  const me = trpc.auth.me.useQuery(undefined, { retry: false });

  const isDark = colorScheme === 'dark';
  const initials =
    [me.data?.firstName?.[0], me.data?.lastName?.[0]].filter(Boolean).join('').toUpperCase() ||
    me.data?.email?.[0]?.toUpperCase() ||
    '?';

  return (
    <Group gap="xs" wrap="nowrap">
      <Tooltip label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
        <ActionIcon
          variant="subtle"
          size="lg"
          aria-label="Toggle color scheme"
          onClick={() => setColorScheme(isDark ? 'light' : 'dark')}
        >
          {isDark ? <IconSun size={18} /> : <IconMoon size={18} />}
        </ActionIcon>
      </Tooltip>

      <Menu position="bottom-end" withinPortal width={180}>
        <Menu.Target>
          <Tooltip label="Language">
            <ActionIcon variant="subtle" size="lg" aria-label="Language">
              <IconLanguage size={18} />
            </ActionIcon>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Language</Menu.Label>
          {LANGUAGES.map((lang) => (
            <Menu.Item
              key={lang.value}
              onClick={() => {
                void i18n.changeLanguage(lang.value);
              }}
              rightSection={
                i18n.resolvedLanguage === lang.value ? (
                  <Text size="xs" c="dimmed">
                    ✓
                  </Text>
                ) : null
              }
            >
              {lang.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>

      <Menu position="bottom-end" withinPortal width={280}>
        <Menu.Target>
          <Avatar
            radius="xl"
            size="md"
            style={{ cursor: 'pointer' }}
            src={me.data?.profilePictureUrl ?? undefined}
          >
            {initials}
          </Avatar>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Signed in</Menu.Label>
          <Stack gap={4} px="sm" py={6}>
            <Text fw={600} size="sm" truncate>
              {me.data?.displayName ||
                [me.data?.firstName, me.data?.lastName].filter(Boolean).join(' ') ||
                me.data?.email}
            </Text>
            <Text size="xs" c="dimmed" truncate>
              {me.data?.email ?? '—'}
            </Text>
            {me.data?.roles && me.data.roles.length > 0 && (
              <Text size="xs" c="blue">
                {me.data.roles.join(' · ')}
              </Text>
            )}
          </Stack>
          <Divider my={4} />
          <Menu.Item leftSection={<IconUser size={14} />} component={Link} to="/auth/profile">
            Profile
          </Menu.Item>
          <Menu.Item
            leftSection={<IconSettings size={14} />}
            component={Link}
            to="/vendor/settings"
          >
            Settings
          </Menu.Item>
          <Divider my={4} />
          <Menu.Item
            leftSection={<IconLogout size={14} />}
            color="red"
            component={Link}
            to="/auth/logout"
          >
            Sign out
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
